import React, { useState } from 'react';
import { Modal, Button, Message, Checkbox, Accordion, Icon } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { useStore } from '../../../core/api/GlobalStoreContext';
import { Label, Input, TextArea } from './FormElements';
import {
  FormSection,
  StyledModal,
  SmallText,
  StyledDropdown,
} from './styles';
import type { CloneRequest } from "@dasha.ai/sdk/src/rest-api/tts";
import type { TtsProvider } from "@dasha.ai/sdk/src/rest-api/generated/core/models";
import { AVAILABLE_LANGUAGES } from '../../../core/api/VoiceConfig';

const StyledAccordion = styled(Accordion)`
  &.ui.accordion {
    margin-bottom: 24px;
    
    .title {
      color: ${({ theme }) => theme.colors.textPrimary};
      background: transparent;
      border: none;
      padding: 8px 0;
      
      &:hover {
        color: ${({ theme }) => theme.colors.textPrimary};
      }
      
      .icon {
        color: ${({ theme }) => theme.colors.textPrimary};
      }
    }
    
    .content {
      padding: 0;
      background: transparent;
      border: none;
    }
  }
`;

const StyledCheckbox = styled(Checkbox)`
  &.ui.checkbox {
    margin-top: 8px;
    
    label {
      color: ${({ theme }) => theme.colors.textPrimary};
      
      &:before {
        background: ${({ theme }) => theme.colors.surfaceHover};
        border: 1px solid ${({ theme }) => theme.colors.border};
      }
      
      &:hover:before {
        background: ${({ theme }) => theme.colors.surfaceHover};
        border-color: ${({ theme }) => theme.colors.accent};
      }
    }
    
    &.checked label:before {
      background: ${({ theme }) => theme.colors.accent} !important;
      border-color: ${({ theme }) => theme.colors.accent} !important;
    }
    
    input:focus ~ label:before {
      background: ${({ theme }) => theme.colors.surfaceHover};
      border-color: ${({ theme }) => theme.colors.accent};
    }

    input:checked ~ label:after {
      color: #fff !important;
    }

    &.disabled {
      opacity: 0.5;
      
      label {
        color: ${({ theme }) => theme.colors.textSecondary} !important;
        cursor: not-allowed;
      }
      
      &:before, label:before {
        background: ${({ theme }) => theme.colors.surface} !important;
        border-color: ${({ theme }) => theme.colors.border} !important;
      }
    }
  }
`;

const SettingsGroup = styled.div`
  margin-top: 16px;
  
  ${Label} {
    margin-bottom: 8px;
    display: block;
  }
`;

const StyledLink = styled.a`
  color: ${({ theme }) => theme.colors.accent};
  text-decoration: underline;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accentHover};
  }
`;

const PROVIDER_REQUIREMENTS = {
  elevenlabs: {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    minDuration: 30, // seconds
    requirements: 'Maximum file size: 10MB, Minimum audio length: 30 seconds',
    consentText: (
      <>
        I hereby confirm that I have all necessary rights or consents to upload and clone these voice samples and that I will not use the platform-generated content for any illegal, fraudulent, or harmful purpose. I reaffirm my obligation to abide by ElevenLabs' <StyledLink href="https://elevenlabs.io/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Service</StyledLink>, <StyledLink href="https://elevenlabs.io/use-policy" target="_blank" rel="noopener noreferrer">Prohibited Content and Uses Policy</StyledLink> and <StyledLink href="https://elevenlabs.io/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</StyledLink>.
      </>
    )
  },
  cartesia: {
    stability: {
      duration: '15-20 seconds',
      enhance: true,
      requirements: 'Audio duration: 15-20 seconds, Enhance Audio: enabled'
    },
    similarity: {
      duration: '5 seconds',
      enhance: false,
      requirements: 'Audio duration: 5 seconds, Enhance Audio: disabled'
    },
    consentText: (
      <>
        By clicking Confirm, you certify that you have consent to clone these voice samples and that you will not use anything generated on Cartesia for illegal or harmful purposes. By proceeding, you agree to Cartesia's <StyledLink href="https://www.cartesia.ai/legal/terms.html" target="_blank" rel="noopener noreferrer">Terms of Service</StyledLink> and <StyledLink href="https://www.cartesia.ai/legal/acceptable-use.html" target="_blank" rel="noopener noreferrer">Acceptable Use Policy</StyledLink>.
      </>
    )
  },
  lmnt: {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    minDuration: 5, // seconds
    requirements: 'Maximum file size: 10MB, Minimum audio length: 10 seconds',
    consentText: (
      <>
        I hereby confirm that I have all necessary rights or consents to upload and clone these voice samples and that I will not use the platform-generated content for any illegal, fraudulent, or harmful purpose. I reaffirm my obligation to abide by LMNT's <StyledLink href="https://www.lmnt.com/legal/tos" target="_blank" rel="noopener noreferrer">Terms of Service</StyledLink>, <StyledLink href="https://www.lmnt.com/legal/aup" target="_blank" rel="noopener noreferrer">Prohibited Content and Uses Policy</StyledLink> and <StyledLink href="https://www.lmnt.com/legal/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</StyledLink>.
      </>
    )
  },
  inworld: {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    minDuration: 3, // seconds
    requirements: 'Recommended 10-15s per clip (clips are trimmed to 15s). You may add multiple samples. WAV or MP3.',
    consentText: (
      <>
        I hereby confirm that I have all necessary rights or consents to upload and clone these voice samples and that I will not use the platform-generated content for any illegal, fraudulent, or harmful purpose. I reaffirm my obligation to abide by Inworld's <StyledLink href="https://inworld.ai/terms" target="_blank" rel="noopener noreferrer">Terms of Service</StyledLink> and <StyledLink href="https://inworld.ai/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</StyledLink>.
      </>
    )
  },
  minimax: {
    maxFileSize: 20 * 1024 * 1024, // 20MB
    minDuration: 10, // seconds
    requirements: 'Formats: MP3/M4A/WAV, Maximum file size: 20MB, Audio length: 10 seconds - 5 minutes',
    consentText: (
      <>
        I hereby confirm that I have all necessary rights or consents to upload and clone these voice samples and that I will not use the platform-generated content for any illegal, fraudulent, or harmful purpose. I reaffirm my obligation to abide by MiniMax's <StyledLink href="https://www.minimax.io/platform/protocol/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</StyledLink> and <StyledLink href="https://www.minimax.io/platform/protocol/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</StyledLink>.
      </>
    )
  },
} as const;

const DEFAULT_PROVIDER_SETTINGS: CloneRequest['providerSpecific'] = {
  elevenLabs: {
    removeBackgroundNoise: true
  },
  cartesia: {
    mode: "Similarity",
    enhance: false
  },
  lmnt: {
    enhance: false
  },
  inworld: {
    removeBackgroundNoise: true
  },
  minimax: {
    needNoiseReduction: false,
    needVolumeNormalization: false,
    accuracy: 0.7
  }
};

interface VoiceCloneModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (voiceId: string, language: string) => void;
}

export const VoiceCloneModal: React.FC<VoiceCloneModalProps> = observer(({
  isOpen,
  onClose,
  onSuccess
}) => {
  const globalStore = useStore();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [newVoiceName, setNewVoiceName] = useState('');
  const [newVoiceDescription, setNewVoiceDescription] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [selectedGender, setSelectedGender] = useState<string>('');
  const [selectedProvider, setSelectedProvider] = useState<TtsProvider | ''>('');
  const [isProviderSettingsOpen, setIsProviderSettingsOpen] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);

  // Provider-specific settings with defaults
  const [providerSpecific, setProviderSpecific] = useState<CloneRequest['providerSpecific']>(DEFAULT_PROVIDER_SETTINGS);

  const resetForm = () => {
    setNewVoiceName('');
    setNewVoiceDescription('');
    setSelectedFiles([]);
    setSelectedLanguage('');
    setSelectedGender('');
    setSelectedProvider('');
    setUploadError('');
    setProviderSpecific(DEFAULT_PROVIDER_SETTINGS);
    setIsProviderSettingsOpen(false);
    setHasConsent(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getAudioDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      const objectUrl = URL.createObjectURL(file);
      audio.addEventListener('loadedmetadata', () => {
        URL.revokeObjectURL(objectUrl);
        resolve(audio.duration);
      });
      audio.addEventListener('error', () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('audio load error'));
      });
      audio.src = objectUrl;
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) {
      setUploadError('Please select a file');
      return;
    }

    const provider = selectedProvider.toLowerCase();

    try {
      for (const file of files) {
        if ((provider === 'elevenlabs' || provider === 'lmnt' || provider === 'inworld') &&
            file.size > PROVIDER_REQUIREMENTS[provider].maxFileSize) {
          setUploadError(`File "${file.name}" exceeds ${PROVIDER_REQUIREMENTS[provider].maxFileSize / (1024 * 1024)}MB limit.`);
          return;
        }

        const duration = await getAudioDuration(file);

        if (provider === 'elevenlabs') {
          if (duration < PROVIDER_REQUIREMENTS.elevenlabs.minDuration) {
            setUploadError(`Audio must be at least ${PROVIDER_REQUIREMENTS.elevenlabs.minDuration} seconds long for ElevenLabs.`);
            return;
          }
        } else if (provider === 'lmnt') {
          if (duration < PROVIDER_REQUIREMENTS.lmnt.minDuration) {
            setUploadError(`Audio must be at least ${PROVIDER_REQUIREMENTS.lmnt.minDuration} seconds long for LMNT.`);
            return;
          }
        } else if (provider === 'inworld') {
          if (duration < PROVIDER_REQUIREMENTS.inworld.minDuration) {
            setUploadError(`Audio "${file.name}" must be at least ${PROVIDER_REQUIREMENTS.inworld.minDuration} seconds long for Inworld.`);
            return;
          }
        } else if (provider === 'cartesia') {
          const mode = providerSpecific.cartesia?.mode ?? 'Similarity';
          const modeSettings = PROVIDER_REQUIREMENTS.cartesia[mode.toLowerCase()];
          if (mode === 'Stability' && (duration < 15 || duration > 20)) {
            setUploadError('For Stability mode, audio must be between 15-20 seconds.');
            return;
          } else if ((mode === 'Similarity' && duration < 3) || duration > 10) {
            setUploadError('For Similarity mode, audio must be approximately 5 seconds, but got ' + duration + ' seconds.');
            return;
          }
          setProviderSpecific(prev => ({
            ...prev,
            cartesia: { ...prev.cartesia, mode, enhance: modeSettings.enhance }
          }));
        }
      }

      // Non-Inworld providers use a single sample.
      setSelectedFiles(provider === 'inworld' ? files : [files[0]]);
      setUploadError('');
    } catch {
      setUploadError('Error validating audio file. Please try another file.');
    }
  };

  const handleVoiceUpload = async () => {
    if (selectedFiles.length === 0 || !newVoiceName || !selectedLanguage || !selectedGender || !selectedProvider || !hasConsent) return;

    setIsUploading(true);
    setUploadError('');

    try {
      const cloneRequest: CloneRequest = {
        provider: selectedProvider,
        name: newVoiceName,
        description: newVoiceDescription || '',
        labels: {
          gender: selectedGender
        },
        providerSpecific,
        language: selectedLanguage,
      };

      const response = await globalStore.voiceStore.createVoice(cloneRequest, selectedFiles);

      if (!response?.voiceId) {
        throw new Error('Failed to create voice: No voice ID returned');
      }

      onSuccess(response.voiceId, selectedLanguage);
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      const errorReason =
        // Check direct reason property
        (error as any)?.reason ||
        // Check response data reason
        (error as any)?.response?.data?.reason ||
        // Check if the entire response.data is the error message
        (typeof (error as any)?.response?.data === 'string' ? (error as any).response.data : '') ||
        // Check other common error response formats
        (error as any)?.response?.data?.error ||
        '';

      setUploadError(
        errorReason ? `${errorMessage}: ${errorReason}` : errorMessage
      );
    } finally {
      setIsUploading(false);
    }
  };

  const renderProviderSpecificSettings = () => {
    switch (selectedProvider.toLowerCase()) {
      case 'elevenlabs':
        return (
          <StyledAccordion>
            <Accordion.Title
              active={isProviderSettingsOpen}
              onClick={() => setIsProviderSettingsOpen(!isProviderSettingsOpen)}
            >
              <Icon name="dropdown" />
              Advanced Settings (Optional)
            </Accordion.Title>
            <Accordion.Content active={isProviderSettingsOpen}>
              <FormSection>
                <StyledCheckbox
                  label="Remove Background Noise"
                  checked={providerSpecific.elevenLabs?.removeBackgroundNoise ?? true}
                  onChange={(_, { checked }) => {
                    setProviderSpecific(prev => ({
                      ...prev,
                      elevenLabs: {
                        ...prev.elevenLabs,
                        removeBackgroundNoise: checked || false
                      }
                    }));
                    setUploadError('');
                  }}
                />
              </FormSection>
            </Accordion.Content>
          </StyledAccordion>
        );

      case 'cartesia':
        const mode = providerSpecific.cartesia?.mode ?? 'Similarity';
        const modeSettings = PROVIDER_REQUIREMENTS.cartesia[mode.toLowerCase()];
        return (
          <StyledAccordion>
            <Accordion.Title
              active={isProviderSettingsOpen}
              onClick={() => setIsProviderSettingsOpen(!isProviderSettingsOpen)}
            >
              <Icon name="dropdown" />
              Advanced Settings (Optional)
            </Accordion.Title>
            <Accordion.Content active={isProviderSettingsOpen}>
              <FormSection>
                <SettingsGroup>
                  <Label>Mode</Label>
                  <StyledDropdown
                    selection
                    fluid
                    options={[
                      { key: 'stability', value: 'Stability', text: 'Stability' },
                      { key: 'similarity', value: 'Similarity', text: 'Similarity' }
                    ]}
                    value={mode}
                    onChange={(_, { value }) => {
                      const newModeSettings = PROVIDER_REQUIREMENTS.cartesia[value.toLowerCase()];
                      setProviderSpecific(prev => ({
                        ...prev,
                        cartesia: {
                          ...prev.cartesia,
                          mode: value as typeof mode,
                          enhance: newModeSettings.enhance
                        }
                      }));
                      setUploadError('');
                    }}
                  />
                </SettingsGroup>
                <StyledCheckbox
                  label="Enhance Audio"
                  checked={providerSpecific.cartesia?.enhance ?? PROVIDER_REQUIREMENTS.cartesia[mode.toLowerCase()].enhance}
                  onChange={(_, { checked }) => {
                    setProviderSpecific(prev => ({
                      ...prev,
                      cartesia: {
                        ...prev.cartesia,
                        enhance: checked || false
                      }
                    }));
                    setUploadError('');
                  }}
                />
                <SettingsGroup>
                  <Label>Transcript (Optional)</Label>
                  <TextArea
                    value={providerSpecific.cartesia?.transcript || ''}
                    onChange={(e, { value }) => {
                      setProviderSpecific(prev => ({
                        ...prev,
                        cartesia: {
                          ...prev.cartesia,
                          transcript: value
                        }
                      }));
                      setUploadError('');
                    }}
                    placeholder="Enter the transcript of the audio file..."
                  />
                </SettingsGroup>
              </FormSection>
            </Accordion.Content>
          </StyledAccordion>
        );

      case 'lmnt':
        return (
          <StyledCheckbox
            label="Enhance Audio"
            checked={providerSpecific.lmnt?.enhance ?? false}
            onChange={(_, { checked }) => {
              setProviderSpecific(prev => ({
                ...prev,
                lmnt: {
                  ...prev.lmnt,
                  enhance: checked || false
                }
              }));
              setUploadError('');
            }}
          />
        );
      case 'inworld':
        return (
          <StyledCheckbox
            label="Remove Background Noise"
            checked={providerSpecific.inworld?.removeBackgroundNoise ?? true}
            onChange={(_, { checked }) => {
              setProviderSpecific(prev => ({
                ...prev,
                inworld: {
                  ...prev.inworld,
                  removeBackgroundNoise: checked || false
                }
              }));
              setUploadError('');
            }}
          />
        );

      case 'minimax':
        return (
          <StyledAccordion>
            <Accordion.Title
              active={isProviderSettingsOpen}
              onClick={() => setIsProviderSettingsOpen(!isProviderSettingsOpen)}
            >
              <Icon name="dropdown" />
              Advanced Settings (Optional)
            </Accordion.Title>
            <Accordion.Content active={isProviderSettingsOpen}>
              <FormSection>
                <StyledCheckbox
                  label="Noise reduction"
                  checked={providerSpecific.minimax?.needNoiseReduction ?? false}
                  onChange={(_, { checked }) => {
                    setProviderSpecific(prev => ({
                      ...prev,
                      minimax: {
                        ...prev.minimax,
                        needNoiseReduction: checked || false
                      }
                    }));
                    setUploadError('');
                  }}
                />
                <StyledCheckbox
                  label="Volume normalization"
                  checked={providerSpecific.minimax?.needVolumeNormalization ?? false}
                  onChange={(_, { checked }) => {
                    setProviderSpecific(prev => ({
                      ...prev,
                      minimax: {
                        ...prev.minimax,
                        needVolumeNormalization: checked || false
                      }
                    }));
                    setUploadError('');
                  }}
                />
                <SettingsGroup>
                  <Label>Accuracy</Label>
                  <Input
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    value={providerSpecific.minimax?.accuracy ?? 0.7}
                    onChange={(e) => {
                      const value = e.target.value === '' ? undefined : Number(e.target.value);
                      setProviderSpecific(prev => ({
                        ...prev,
                        minimax: {
                          ...prev.minimax,
                          accuracy: value
                        }
                      }));
                      setUploadError('');
                    }}
                  />
                </SettingsGroup>
              </FormSection>
            </Accordion.Content>
          </StyledAccordion>
        );
      default:
        return null;
    }
  };

  const renderConsentSection = () => {
    if (!selectedProvider) return null;

    const consentText = selectedProvider.toLowerCase() === 'elevenlabs'
      ? PROVIDER_REQUIREMENTS.elevenlabs.consentText
      : selectedProvider.toLowerCase() === 'cartesia'
        ? PROVIDER_REQUIREMENTS.cartesia.consentText
        : selectedProvider.toLowerCase() === 'lmnt'
          ? PROVIDER_REQUIREMENTS.lmnt.consentText
          : selectedProvider.toLowerCase() === 'inworld'
            ? PROVIDER_REQUIREMENTS.inworld.consentText
          : selectedProvider.toLowerCase() === 'minimax'
            ? PROVIDER_REQUIREMENTS.minimax.consentText
            : '';

    if (!consentText) return null;

    return (
      <FormSection>
        <StyledAccordion>
          <Accordion.Title active={true}>
            <Icon name="warning circle" />
            Terms & Conditions
          </Accordion.Title>
          <Accordion.Content active={true}>
            <div style={{ marginBottom: '16px' }}>
              <SmallText>{consentText}</SmallText>
            </div>
            <StyledCheckbox
              label="I agree to the terms and conditions"
              checked={hasConsent}
              onChange={(_, { checked }) => {
                setHasConsent(checked || false);
                setUploadError('');
              }}
            />
          </Accordion.Content>
        </StyledAccordion>
      </FormSection>
    );
  };

  const languageOptions = AVAILABLE_LANGUAGES.map(lang => ({
    key: lang.value,
    value: lang.value,
    text: lang.label,
    flag: lang.flag
  }));

  const genderOptions = [
    { key: 'female', value: 'female', text: 'Female' },
    { key: 'male', value: 'male', text: 'Male' }
  ];

  return (
    <StyledModal
      open={isOpen}
      onClose={handleClose}
      size="small"
    >
      <Modal.Header>Clone New Voice</Modal.Header>
      <Modal.Content>
        <FormSection>
          <Label>Provider *</Label>
          <StyledDropdown
            selection
            fluid
            error={!selectedProvider}
            options={globalStore.voiceStore?.getAvailableProvidersWithClone().map(provider => ({
              key: provider,
              value: provider as TtsProvider,
              text: provider
            }))}
            value={selectedProvider}
            onChange={(_, { value }) => {
              setSelectedProvider(value as TtsProvider);
              setProviderSpecific({});
              setUploadError('');
            }}
            placeholder="Select voice provider..."
          />
        </FormSection>

        <FormSection>
          <Label>Voice Name *</Label>
          <Input
            type="text"
            value={newVoiceName}
            onChange={(e) => {
              setNewVoiceName(e.target.value);
              setUploadError('');
            }}
            placeholder="Enter a name for your cloned voice, for example: 'Mary' or 'John'"
          />
        </FormSection>

        <FormSection>
          <Label>Language *</Label>
          <StyledDropdown
            selection
            fluid
            options={languageOptions}
            value={selectedLanguage}
            onChange={(_, { value }) => {
              setSelectedLanguage(value as string);
              setUploadError('');
            }}
            placeholder="Select speaker's native language..."
            error={!selectedLanguage}
          />
        </FormSection>

        <FormSection>
          <Label>Gender *</Label>
          <StyledDropdown
            selection
            fluid
            options={genderOptions}
            value={selectedGender}
            onChange={(_, { value }) => {
              setSelectedGender(value as string);
              setUploadError('');
            }}
            placeholder="Select speaker's gender..."
            error={!selectedGender}
          />
        </FormSection>

        <FormSection>
          <Label>Description *</Label>
          <TextArea
            value={newVoiceDescription}
            onChange={(e) => {
              setNewVoiceDescription(e.target.value);
              setUploadError('');
            }}
            placeholder="Describe the voice, for example: 'Young 20-year-old female voice, friendly and cheerful, speaks with a soft and gentle accent.'"
          />
        </FormSection>

        {selectedProvider && renderProviderSpecificSettings()}

        <FormSection>
          <Label>Audio File{selectedProvider.toLowerCase() === 'inworld' ? 's' : ''} *</Label>
          <Input
            type="file"
            accept="audio/*"
            multiple={selectedProvider.toLowerCase() === 'inworld'}
            onChange={handleFileSelect}
          />
          <SmallText>
            {selectedProvider && (
              <strong>Provider Requirements: </strong>
            )}
            {selectedProvider.toLowerCase() === 'elevenlabs'
              ? PROVIDER_REQUIREMENTS.elevenlabs.requirements
              : selectedProvider.toLowerCase() === 'lmnt'
                ? PROVIDER_REQUIREMENTS.lmnt.requirements
                : selectedProvider.toLowerCase() === 'inworld'
                  ? PROVIDER_REQUIREMENTS.inworld.requirements
                  : selectedProvider.toLowerCase() === 'cartesia'
                    ? PROVIDER_REQUIREMENTS.cartesia[
                      (providerSpecific.cartesia?.mode ?? 'Similarity').toLowerCase()
                    ].requirements
                    : 'Please select a provider first'}
          </SmallText>
        </FormSection>

        {selectedProvider && renderConsentSection()}
      </Modal.Content>
      <Modal.Actions>
        {uploadError && (
          <Message negative style={{ marginBottom: '1rem', width: '100%' }}>
            <Message.Header>Error</Message.Header>
            <p>{uploadError}</p>
          </Message>
        )}
        <Button secondary onClick={handleClose}>
          Cancel
        </Button>
        <Button
          primary
          loading={isUploading}
          disabled={selectedFiles.length === 0 || !newVoiceName || !selectedLanguage || !selectedGender || !selectedProvider || !hasConsent || isUploading}
          onClick={handleVoiceUpload}
        >
          Clone Voice
        </Button>
      </Modal.Actions>
    </StyledModal>
  );
}); 